import { createFileRoute, Link } from "@tanstack/react-router";
import { Section } from "@/components/Section";
import { Reveal } from "@/components/Reveal";
import { ArrowUpRight, Loader2 } from "lucide-react";
import { useSiteAudit } from "@/components/audit/useSiteAudit";
import { AuditCheckList } from "@/components/audit/AuditCheckList";
import { buildSeo, breadcrumbSchema } from "@/lib/seo";
import { JsonLd } from "@/components/JsonLd";
import { SITE } from "@/content/site";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { ReviewedBy } from "@/components/ReviewedBy";
import { RangpurAreasStrip } from "@/components/RangpurAreasStrip";

export const Route = createFileRoute("/free-seo-audit")({
  head: () => buildSeo({
    title: "Free SEO Audit Tool — Instant Website SEO Checker | Freelancer Tamal",
    description: "Get a free instant SEO audit of any URL — title tags, meta descriptions, headings, indexability, Core Web Vitals signals, and quick-win opportunities. No signup.",
    path: "/free-seo-audit",
    alternates: { bn: "/bn/free-seo-audit" },
    ogEyebrow: "Free Audit",
    keywords: "free SEO audit, free SEO checker, website SEO analyzer, instant SEO audit tool",
  }),
  component: AuditTool,
});

function AuditTool() {
  const { url, setUrl, loading, result, error, onSubmit } = useSiteAudit({
    invalid: (validationError) => validationError,
    serverError: (serverError) => serverError,
    failed: "The audit couldn't complete. Try again in a moment.",
  });

  return (
    <>
      <Breadcrumbs items={[{ name: "Home", path: "/" }, { name: "Free SEO Audit", path: "/free-seo-audit" }]} />
      <h1 className="sr-only">Free SEO Audit Tool</h1>
      <Section eyebrow="Free SEO Audit Tool" title={<>Get an instant <span className="text-gradient">SEO snapshot.</span></>}>
        <p className="max-w-2xl text-lg text-muted-foreground -mt-8">
          Drop any public URL below. The audit fetches the page live and reports
          only what it can actually see in the response — status and redirects,
          title and meta, headings, schema, alt coverage, robots and sitemap
          reachability, response time and page weight. Want the full 200-point
          audit? <Link to="/contact" className="text-foreground underline underline-offset-4">Book a paid audit.</Link>
        </p>
        <ReviewedBy reviewedOn="2026-06-01" credentials="SEO, AEO and GEO consultant based in Rangpur, Bangladesh" />

        <div className="mt-8 rounded-3xl border border-primary/40 bg-primary/5 p-6 lg:p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 max-w-3xl">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-primary">Full Audit Tool</div>
            <div className="font-display text-xl mt-1">Run the complete free audit on tool.freelancertamal.com</div>
            <div className="text-sm text-muted-foreground mt-1">Deeper crawl, Core Web Vitals, on-page, and technical checks — free and no signup.</div>
          </div>
          <a
            href="https://tool.freelancertamal.com/"
            target="_blank"
            rel="noopener"
            className="inline-flex flex-none items-center gap-2 rounded-full bg-primary text-primary-foreground px-6 py-3 text-sm font-medium hover:shadow-[var(--shadow-glow)]"
          >
            Open free tool <ArrowUpRight size={14} />
          </a>
        </div>

        <form onSubmit={onSubmit} className="mt-10 flex flex-col sm:flex-row gap-3 max-w-2xl">
          <label htmlFor="audit-url" className="sr-only">Website URL to audit</label>
          <input
            id="audit-url"
            type="text"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="yourdomain.com"
            className="flex-1 rounded-full border border-border bg-card px-6 py-4 text-sm focus:outline-none focus:border-primary/60"
          />
          <button
            type="submit"
            disabled={loading}
            className="inline-flex items-center justify-center gap-2 rounded-full bg-primary text-primary-foreground px-7 py-4 text-sm font-medium hover:shadow-[var(--shadow-glow)] disabled:opacity-60"
          >
            {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Fetching page</> : <>Run audit <ArrowUpRight size={16} /></>}
          </button>
        </form>

        {error && <p role="alert" className="mt-4 text-sm text-destructive max-w-2xl">{error}</p>}

        {result && (
          <Reveal>
            <div className="mt-12 rounded-3xl border border-border bg-card p-8 lg:p-12">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                <div>
                  <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Live audit of</div>
                  <div className="font-display text-2xl mt-1 break-all">{result.finalUrl}</div>
                  <div className="text-sm text-muted-foreground mt-1">
                    HTTP {result.statusCode} · {result.ms} ms · {(result.bytes / 1024).toFixed(0)} KB of HTML
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Checks passed</div>
                  <div className="font-display text-5xl text-gradient">{result.passed}/{result.total}</div>
                  <div className="text-sm text-muted-foreground">{result.score}% score</div>
                </div>
              </div>

              <AuditCheckList checks={result.checks} className="mt-10 divide-y divide-border" />

              <p className="mt-8 text-xs text-muted-foreground">
                Everything above is read from a single live fetch of the URL, so it covers what a
                crawler sees in the raw HTML. It does not run JavaScript, crawl internal links, or
                measure field Core Web Vitals — those need the full audit.
              </p>

              <div className="mt-6 rounded-2xl border border-primary/30 bg-primary/5 p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                  <div className="font-display text-xl">Want the real audit?</div>
                  <div className="text-sm text-muted-foreground mt-1">200 checks, log file analysis, and a prioritized fix-list.</div>
                </div>
                <Link to="/contact" className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-6 py-3 text-sm font-medium">
                  Book paid audit <ArrowUpRight size={14} />
                </Link>
              </div>
            </div>
          </Reveal>
        )}

      </Section>

      <Section className="!pt-0 !pb-12">
        <div className="rounded-2xl border border-border p-6">
          <h2 className="font-display text-xl">Share this free tool</h2>
          <p className="mt-2 text-sm text-muted-foreground">Run a blog, agency or community? Copy this snippet to recommend the free SEO audit to your readers.</p>
          <pre className="mt-4 overflow-x-auto rounded-lg bg-muted p-4 text-xs"><code>{'<a href="https://freelancertamal.com/free-seo-audit">Free SEO audit tool by Freelancer Tamal</a>'}</code></pre>
        </div>
      </Section>

      <RangpurAreasStrip
        eyebrow="Free Audit · Rangpur"
        title={<>Auditing a Rangpur <span className="text-gradient">business site?</span></>}
        description="Pair the free audit with a hyperlocal SEO playbook for your exact area — pick your neighborhood below and see the local-pack and entity-signal moves that work there."
      />
      <JsonLd data={{
        "@context": "https://schema.org",
        "@type": "WebApplication",
        name: "Free SEO Audit Tool",
        url: `${SITE.url}/free-seo-audit`,
        applicationCategory: "BusinessApplication",
        operatingSystem: "Any",
        browserRequirements: "Requires JavaScript",
        description: "Free instant SEO audit — checks title tags, meta descriptions, headings, indexability, Core Web Vitals signals, and quick-win opportunities for any public URL.",
        offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
        creator: { "@type": "Person", "@id": `${SITE.url}/#person`, name: SITE.fullName, url: `${SITE.url}/about` },
        
      }} />
      <JsonLd data={breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Free SEO Audit", path: "/free-seo-audit" }])} />
    </>
  );
}
